<template>
  <ErrorBoundary 
    :error-title="t('profile.errorTitle')" 
    :error-message="t('profile.errorMessage')"
  >
    <div class="p-2 mt-6">
      <BlogMeta 
        :title="t('profile.metaTitle')"
        :description="t('profile.metaDescription')"
        :noIndex="true"
      />
      

      <!-- Contenedor principal del perfil -->
      <div class="max-w-lg mx-auto bg-white rounded-lg shadow-sm p-1 sm:p-5 overflow-hidden">
        <div class="py-8 px-4 sm:px-10">
          <!-- Título del perfil y botón editar en la misma fila -->
          <div class="flex items-center justify-between mb-6">
            <h1 class="text-2xl font-bold text-gray-900">
              {{ t('profile.pageTitle') }}
            </h1>
            <div v-if="!isEditing">
              <button @click="toggleEdit" class="inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-indigo-700 bg-white ">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                {{ t('profile.editButton') }}
              </button>
            </div>
          </div>
          
          <form @submit.prevent="updateProfile" class="space-y-6">
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2 text-center">{{ t('profile.userTypeLabel') }}</label>
              <p v-if="!isEditing" class="text-gray-600 text-center">
                {{ 
                  userProfile.userType === 'owner' 
                    ? t('profile.userTypes.owner') 
                    : userProfile.userType === 'agent'
                      ? t('profile.userTypes.agent')
                      : t('profile.userTypes.applicant') 
                }}
              </p>
              <!-- Si es agente, mostrar solo texto sin posibilidad de cambio -->
              <div v-else-if="userProfile.userType === 'agent'" class="mt-2 text-center">
                <span class="inline-flex items-center px-6 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-indigo-700 to-indigo-700 text-white shadow-sm">
                  Agente
                </span>
                <p class="mt-2 text-xs text-gray-500">Los agentes no pueden cambiar su tipo de usuario</p>
              </div>
              <!-- Si no es agente, mostrar opciones de cambio -->
              <div v-else class="mt-2 flex justify-center">
                <div class="inline-flex rounded-lg p-1 bg-white shadow-sm space-x-2">
                  <button 
                    type="button"
                    @click="userProfile.userType = 'owner'"
                    :class="[
                      'px-6 py-2 text-sm font-medium rounded-full transition-all duration-200',
                      userProfile.userType === 'owner'
                        ? 'bg-gradient-to-r from-indigo-700 to-indigo-700 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    ]"
                  >
                    {{ t('profile.userTypes.owner') }}
                  </button>
                  <button 
                    type="button"
                    @click="userProfile.userType = 'applicant'"
                    :class="[
                      'px-6 py-2 text-sm font-medium rounded-full transition-all duration-200',
                      userProfile.userType === 'applicant'
                        ? 'bg-gradient-to-r from-indigo-700 to-indigo-700 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    ]"
                  >
                    {{ t('profile.userTypes.applicant') }}
                  </button>
                </div>
              </div>
            </div>
            
            <div class="mb-4">
              <label for="fullName" class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.fullNameLabel') }}</label>
              <input v-if="isEditing" v-model="userProfile.fullName" type="text" id="fullName" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900" required>
              <p v-else class="text-gray-600">{{ userProfile.fullName }}</p>
            </div>
            
            <div v-if="false" class="mb-4">
              <label for="dateOfBirth" class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.dateOfBirthLabel') }}</label>
              <p v-if="!isEditing" class="text-gray-600">{{ formatDate(userProfile.dateOfBirth) }}</p>
              <div v-else class="relative">
                <input 
                  v-model="dateOfBirthInput" 
                  type="text" 
                  id="dateOfBirthInput" 
                  :placeholder="t('profile.dateOfBirthPlaceholder')" 
                  @input="handleDateInput"
                  class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900" 
                  required
                >
                <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                  <button 
                    type="button" 
                    class="text-gray-400 hover:text-gray-500 focus:outline-none"
                    @click="showDatePicker = !showDatePicker"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
                <!-- Date picker visual mejorado -->
                <div 
                  v-if="showDatePicker" 
                  class="absolute z-10 mt-1 w-full sm:w-72 bg-white shadow-lg rounded-md border border-gray-200"
                >
                  <div class="p-4">
                    <div class="mb-4">
                      <!-- Cabecera del calendario con selector de año -->
                      <div class="flex justify-between items-center mb-3">
                        <!-- Botón mes anterior -->
                        <button 
                          type="button" 
                          class="p-1.5 rounded-full hover:bg-gray-100 text-gray-700"
                          @click="changeMonth(-1)"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                          </svg>
                        </button>
                        
                        <!-- Selector de mes y año -->
                        <div class="flex flex-col items-center">
                          <!-- Mes actual (siempre visible) -->
                          <span class="font-medium text-gray-800">{{ currentMonthName }}</span>
                          
                          <!-- Año (clickeable para mostrar selector) -->
                          <button 
                            type="button"
                            class="text-sm font-medium text-gray-700 hover:text-indigo-600 flex items-center"
                            @click="showYearSelector = !showYearSelector"
                          >
                            {{ currentYear }}
                            <svg v-if="!showYearSelector" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        
                        <!-- Botón mes siguiente -->
                        <button 
                          type="button" 
                          class="p-1.5 rounded-full hover:bg-gray-100 text-gray-700"
                          @click="changeMonth(1)"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                          </svg>
                        </button>
                      </div>
                      
                      <!-- Selector de años (estilo iOS) -->
                      <div v-if="showYearSelector" class="py-2 mb-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                        <div class="grid grid-cols-3 gap-1">
                          <button
                            v-for="year in availableYears"
                            :key="year"
                            @click="selectYear(year)"
                            class="py-1.5 px-2 mx-1 text-sm rounded"
                            :class="year === currentYear ? 'bg-indigo-600 text-white font-medium' : 'text-gray-700 hover:bg-gray-100'"
                          >
                            {{ year }}
                          </button>
                        </div>
                      </div>
                      
                      <!-- Días de la semana -->
                      <div class="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-700 mt-2">
                        <span>Lu</span>
                        <span>Ma</span>
                        <span>Mi</span>
                        <span>Ju</span>
                        <span>Vi</span>
                        <span>Sa</span>
                        <span>Do</span>
                      </div>
                    </div>
                    
                    <!-- Días del mes -->
                    <div class="grid grid-cols-7 gap-1">
                      <template v-for="day in calendarDays" :key="day.date">
                        <button
                          type="button"
                          :class="[
                            'h-8 w-8 rounded-full flex items-center justify-center text-sm',
                            day.isCurrentMonth ? 'text-gray-800' : 'text-gray-400',
                            day.isSelected ? 'bg-indigo-600 text-white font-medium hover:bg-indigo-700' : '',
                            day.isDisabled ? 'text-gray-300 cursor-not-allowed' : '',
                            !day.isDisabled && !day.isSelected && day.isCurrentMonth ? 'hover:bg-gray-100' : ''
                          ]"
                          :disabled="day.isDisabled"
                          @click="selectDate(day)"
                        >
                          {{ day.day }}
                        </button>
                      </template>
                    </div>

                    <div class="mt-4 flex justify-center">
                      <button 
                        type="button" 
                        class="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                        @click="showDatePicker = false"
                      >
                        {{ t('profile.datePickerClose') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <p v-if="dateError" class="mt-1 text-sm text-red-600">{{ dateError }}</p>
              <p v-else-if="isEditing" class="mt-1 text-sm text-gray-500">{{ t('profile.dateAgeRequirement') }}</p>
            </div>

            <!-- OCULTO PARA AGENTES: Campo DNI (los agentes usan AgentProfile.vue) -->
            <div v-if="userProfile.userType !== 'agent'" class="mb-4">
              <label for="dni" class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.dniLabel') }}</label>
              <input 
                v-if="isEditing" 
                v-model="userProfile.dni" 
                type="text" 
                id="dni" 
                @input="handleDniInput"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                :class="{ 'border-red-500': dniError }"
                required
                :placeholder="t('profile.dniPlaceholder')"
              >
              <p v-if="dniError" class="mt-1 text-sm text-red-600">{{ dniError }}</p>
              <p v-if="dniValidationMessage" class="mt-1 text-sm text-green-600">{{ dniValidationMessage }}</p>
              
              <p v-else class="text-gray-600">{{ userProfile.dni }}</p>
            </div>
            
            <div class="mb-4">
              <label for="email" class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.emailLabel') }}</label>
              <div v-if="!isEditing" class="flex items-center justify-between">
                <div class="flex items-center">
                  <p class="text-gray-600">{{ userProfile.email }}</p>
                  <span v-if="userProfile.emailVerified" class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <svg class="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                    Verificado
                  </span>
                  <span v-else class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <svg class="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                    Pendiente verificación
                  </span>
                </div>
                <button 
                  @click="showEmailChangeModal = true"
                  class="ml-4 text-sm text-indigo-600 hover:text-indigo-500 font-medium"
                >
                  Cambiar email
                </button>
              </div>
              <input v-else v-model="userProfile.email" type="email" id="email" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900" required>
            </div>
            
            <div v-if="isEditing" class="mb-4">
              <label for="newPassword" class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.newPasswordLabel') }}</label>
              <input 
                v-model="newPassword" 
                type="password" 
                id="newPassword" 
                minlength="8"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
              >
              <p class="mt-1 text-sm text-gray-500">{{ t('profile.passwordRequirement') }}</p>
            </div>
            
            <div v-if="isEditing" class="mb-4">
              <label for="confirmNewPassword" class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.confirmPasswordLabel') }}</label>
              <input 
                v-model="confirmNewPassword" 
                type="password" 
                id="confirmNewPassword" 
                minlength="8"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
              >
            </div>
            
            <div v-if="userProfile.userType === 'owner'" class="mb-4">
              <label for="bankNumber" class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.bankNumberLabel') }} <span class="text-gray-500 text-sm">{{ t('profile.bankNumberInfo') }}</span></label>
              <input 
                v-if="isEditing" 
                v-model="userProfile.bankNumber" 
                type="text" 
                id="bankNumber" 
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                :class="{ 'border-red-500': bankNumberError }"
                :required="userProfile.userType === 'owner'"
                placeholder="ES12 3456 7890 1234 5678 90"
                @input="formatBankNumber"
              >
              <p v-if="bankNumberError" class="mt-1 text-sm text-red-600">{{ bankNumberError }}</p>
              <p v-else-if="isEditing" class="mt-1 text-sm text-gray-500">Formato: IBAN español (ES + 22 dígitos)</p>
              <p v-else class="text-gray-600">{{ userProfile.bankNumber || t('profile.bankNumberNotSpecified') }}</p>
            </div>
            
            <div v-if="userProfile.userType === 'applicant'" class="mb-4">
              <label for="applicantDescription" class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.applicantDescriptionLabel') }}</label>
              <textarea 
                v-if="isEditing" 
                v-model="userProfile.applicantDescription" 
                id="applicantDescription" 
                rows="3" 
                maxlength="200"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                :placeholder="t('profile.applicantDescriptionPlaceholder')"
              ></textarea>
              <p v-else class="text-gray-600">{{ userProfile.applicantDescription || t('profile.applicantDescriptionNotSpecified') }}</p>
              <p v-if="isEditing" class="mt-1 text-sm text-gray-500">
                {{ t('profile.charCounter', { count: userProfile.applicantDescription ? userProfile.applicantDescription.length : 0 }) }}
              </p>
            </div>
            
            <div v-if="userProfile.userType === 'applicant'" class="mb-4">
              <label for="monthlyIncome" class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.monthlyIncomeLabel') }}</label>
              <input 
                v-if="isEditing" 
                v-model.number="userProfile.monthlyIncome" 
                type="number" 
                id="monthlyIncome" 
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                :placeholder="t('profile.monthlyIncomePlaceholder')"
              >
              <p v-else class="text-gray-600">{{ userProfile.monthlyIncome ? `${userProfile.monthlyIncome} €` : t('profile.monthlyIncomeNotSpecified') }}</p>
            </div>
            
            <div v-if="userProfile.userType === 'applicant'" class="mb-4">
              <label for="address" class="block text-sm font-medium text-gray-700 mb-2">Dirección habitual</label>
              <input 
                v-if="isEditing" 
                v-model="userProfile.address" 
                type="text" 
                id="address" 
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                :placeholder="t('profile.addressPlaceholder')"
              >
              <p v-else class="text-gray-600">{{ userProfile.address || t('profile.addressNotSpecified') }}</p>
            </div>
            
            <div v-if="userProfile.userType === 'applicant'" class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.withOtherPersonLabel') }}</label>
              <div v-if="isEditing" class="mt-2">
                <label class="inline-flex items-center">
                  <input 
                    v-model="userProfile.withOtherPerson" 
                    type="checkbox" 
                    class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  >
                  <span class="ml-2 text-gray-700">{{ t('profile.withOtherPersonCheckbox') }}</span>
                </label>
              </div>
              <p v-else class="text-gray-600">{{ userProfile.withOtherPerson ? t('profile.yes') : t('profile.no') }}</p>
            </div>
            
            <div v-if="userProfile.userType === 'applicant' && userProfile.withOtherPerson" class="mb-4">
              <label for="otherPersonName" class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.otherPersonNameLabel') }}</label>
              <input 
                v-if="isEditing" 
                v-model="userProfile.otherPersonName" 
                type="text" 
                id="otherPersonName" 
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                :placeholder="t('profile.otherPersonNamePlaceholder')"
              >
              <p v-else class="text-gray-600">{{ userProfile.otherPersonName || t('profile.otherPersonNameNotSpecified') }}</p>
            </div>
            
            <div v-if="userProfile.userType === 'applicant' && userProfile.withOtherPerson" class="mb-4">
              <label for="otherPersonDni" class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.otherPersonDniLabel') }}</label>
              <input 
                v-if="isEditing" 
                v-model="userProfile.otherPersonDni" 
                type="text" 
                id="otherPersonDni" 
                pattern="[0-9]{8}[A-Z]{1}"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                :required="userProfile.withOtherPerson"
                :placeholder="t('profile.otherPersonDniPlaceholder')"
              >
              <p v-if="isEditing" class="mt-1 text-sm text-gray-500">{{ t('profile.otherPersonDniFormat') }}</p>
              <p v-else class="text-gray-600">{{ userProfile.otherPersonDni || t('profile.otherPersonDniNotSpecified') }}</p>
            </div>
            
            <!-- OCULTO PARA AGENTES: Foto DNI (los agentes usan AgentProfile.vue) -->
            <div v-if="userProfile.userType !== 'agent'" class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('profile.dniPhotoLabel') }}</label>
              <div class="grid grid-cols-1 gap-4">
                <div>
                  <label for="dniFrontPicture" class="block text-gray-700 text-sm mb-2">{{ t('profile.dniFrontLabel') }}</label>
                  <input v-if="isEditing" type="file" id="dniFrontPicture" @change="handleFileUpload('front')" accept="image/*" class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100">
                  <div class="mt-2 flex justify-center">
                    <img v-if="userProfile.dniFrontPictureUrl" :src="userProfile.dniFrontPictureUrl" :alt="t('profile.dniFrontLabel')" class="max-w-full h-auto rounded-lg shadow-md">
                    <div v-else class="flex items-center justify-center w-full h-32 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                      <p class="text-gray-500 text-sm">{{ t('profile.noImage') }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="mb-6">
              <label class="inline-flex items-center">
                <input v-model="userProfile.agreeTerms" type="checkbox" class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" :disabled="!isEditing" required>
                <span class="ml-2 text-gray-700">{{ t('profile.agreeTerms') }}</span>
              </label>
            </div>
            
            <!-- Sección de información del agente para propietarios gestionados -->
            <div v-if="isManagedOwner && (agentData || isLoadingAgent)" class="mt-8 border-t border-gray-200 pt-6">
              <h3 class="text-lg font-medium text-gray-900 mb-4">{{ t('profile.managedBy') || 'Gestionado por' }}</h3>
              
              <div v-if="isLoadingAgent" class="flex justify-center items-center py-4">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
              
              <div v-else-if="agentData" class="bg-indigo-50 rounded-lg border border-indigo-100 p-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <div class="h-12 w-12 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                      {{ agentData.fullName ? agentData.fullName.slice(0, 2).toUpperCase() : 'AG' }}
                    </div>
                  </div>
                  <div class="ml-4">
                    <h4 class="text-base font-medium text-gray-900">{{ agentData.fullName }}</h4>
                    <p v-if="agentData.agencyName" class="text-sm text-gray-600">{{ agentData.agencyName }}</p>
                    <p class="text-sm text-gray-600">{{ agentData.email }}</p>
                  </div>
                </div>
                <div class="mt-3 text-sm text-gray-600">
                  <p>{{ t('profile.agentManagesProperties') || 'Este agente gestiona tus propiedades y contratos.' }}</p>
                </div>
              </div>
            </div>
            
            <div v-if="isEditing" class="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button type="button" @click="cancelEdit" class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-full shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200">
                {{ t('profile.cancelButton') }}
              </button>
              <button type="submit" class="inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200">
                {{ t('profile.updateButton') }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Modal de cambio de email -->
      <div v-if="showEmailChangeModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div class="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
          <div class="mt-3">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-gray-900">
                Cambiar Correo Electrónico
              </h3>
              <button @click="closeEmailChangeModal" class="text-gray-400 hover:text-gray-600">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div v-if="emailChangeStep === 'input'" class="space-y-4">
              <div>
                <label for="newEmail" class="block text-sm font-medium text-gray-700 mb-1">
                  Nuevo correo electrónico
                </label>
                <input 
                  v-model="newEmail" 
                  type="email" 
                  id="newEmail" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="nuevo@email.com"
                  required
                >
                <p v-if="emailChangeError" class="mt-1 text-sm text-red-600">{{ emailChangeError }}</p>
              </div>
              
              <div class="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm text-blue-700">
                      Se enviará un correo de verificación a la nueva dirección. Tu email actual seguirá siendo válido hasta que verifiques el nuevo.
                    </p>
                  </div>
                </div>
              </div>
              
              <div class="flex space-x-3">
                <button 
                  @click="closeEmailChangeModal"
                  class="flex-1 inline-flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button 
                  @click="requestEmailChange"
                  :disabled="isChangingEmail || !newEmail"
                  class="flex-1 inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors duration-200"
                >
                  <LoadingSpinner v-if="isChangingEmail" size="small" color="white" class="mr-2" />
                  <span v-if="isChangingEmail">Enviando...</span>
                  <span v-else>Enviar Verificación</span>
                </button>
              </div>
            </div>
            
            <div v-else-if="emailChangeStep === 'sent'" class="text-center space-y-4">
              <div class="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg class="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h4 class="text-lg font-medium text-gray-900">¡Verificación Enviada!</h4>
                <p class="mt-2 text-sm text-gray-600">
                  Hemos enviado un correo de verificación a <strong>{{ newEmail }}</strong>. 
                  Haz clic en el enlace del correo para cambiar tu dirección de email.
                </p>
              </div>
              <div class="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm text-yellow-700">
                      Tu email actual ({{ userProfile.email }}) seguirá siendo válido hasta que confirmes el cambio.
                    </p>
                  </div>
                </div>
              </div>
              <button 
                @click="closeEmailChangeModal"
                class="w-full inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <ToastComponent v-if="toastMessage" :message="toastMessage" />
    </div>
  </ErrorBoundary>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getAuth, updatePassword, updateEmail, updateProfile as updateAuthProfile } from 'firebase/auth'
import { doc, getDoc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '../firebase'
import ToastComponent from './ToastComponent.vue'
import ErrorBoundary from './ErrorBoundary.vue'
import LoadingSpinner from './LoadingSpinner.vue'
import { validateDNIorNIE } from '../utils/dniValidator'
import { validateSpanishIBAN, formatIBANInput } from '../utils/ibanValidator'
import BlogMeta from './BlogMeta.vue'
import { getCurrentUserManagedOwner, getManagedOwnerAgent } from '../services/managedOwnersService'

export default {
  name: 'ProfilePage',
  components: {
    ToastComponent,
    ErrorBoundary,
    LoadingSpinner,
    BlogMeta
  },
  setup() {
    const { t } = useI18n()

    const userProfile = ref({
      monthlyIncome: null,
      withOtherPerson: false,
      otherPersonName: '',
      applicantDescription: '',
      address: '',
    })
    const newPassword = ref('')
    const confirmNewPassword = ref('')
    const isEditing = ref(false)
    const originalProfile = ref({})
    const toastMessage = ref('')
    const isLoading = ref(false)

    const dniError = ref('')
    const dniValidationMessage = ref('')
    const bankNumberError = ref('')

    // Añadir propiedades para el agente
    const isManagedOwner = ref(false);
    const managedOwnerData = ref(null);
    const agentData = ref(null);
    const isLoadingAgent = ref(false);

    // Variables para cambio de email
    const showEmailChangeModal = ref(false);
    const emailChangeStep = ref('input'); // 'input' | 'sent'
    const newEmail = ref('');
    const emailChangeError = ref('');
    const isChangingEmail = ref(false);

    const fetchUserProfile = async () => {
      // Manejo de perfil simulado en desarrollo
      if (process.env.VUE_APP_ENABLE_SIGNUP_SIMULATION === 'true') {
        const devUser = localStorage.getItem('devUser')
        if (devUser) {
          const parsedUser = JSON.parse(devUser)
          const userDoc = await getDoc(doc(db, 'users', parsedUser.uid))
          if (userDoc.exists()) {
            userProfile.value = userDoc.data()
            originalProfile.value = { ...userDoc.data() }
          }
          return
        }
      }

      const auth = getAuth()
      const user = auth.currentUser
      if (user) {
        try {
          isLoading.value = true;
        const userDoc = await getDoc(doc(db, 'users', user.uid))
          if (userDoc.exists()) {
            const userData = userDoc.data()
            userProfile.value = { ...userData }
            originalProfile.value = { ...userData }
            
            // Si el usuario tiene managedOwnerId, es un propietario gestionado
            if (userData.managedOwnerId) {
              isManagedOwner.value = true;
              await loadAgentInfo(userData.managedOwnerId);
            } else {
              // También verificamos si hay un managedOwner asociado
              try {
                const managedOwner = await getCurrentUserManagedOwner();
                if (managedOwner) {
                  isManagedOwner.value = true;
                  managedOwnerData.value = managedOwner;
                  await loadAgentInfo(managedOwner.id);
                }
              } catch (error) {
                console.error('Error al obtener managedOwner:', error);
              }
            }
        }
        } catch (error) {
          console.error('Error al obtener el perfil del usuario:', error);
        } finally {
          isLoading.value = false;
        }
      }
    }

    // Función para cargar información del agente
    const loadAgentInfo = async (managedOwnerId) => {
      try {
        isLoadingAgent.value = true;
        const agent = await getManagedOwnerAgent(managedOwnerId);
        if (agent) {
          agentData.value = agent;
        }
      } catch (error) {
        console.error('Error al obtener información del agente:', error);
      } finally {
        isLoadingAgent.value = false;
      }
    };

    const validateDniInput = (value) => {
      if (!value) {
        dniError.value = t('profile.dniErrorRequired')
        return false
      }

      // Convertir a mayúsculas
      value = value.toUpperCase().trim()

      // Verificar formato básico
      if (!/^[0-9]{8}[A-Z]$/.test(value) && !/^[XYZ][0-9]{7}[A-Z]$/.test(value)) {
        dniError.value = t('profile.dniErrorFormat')
        return false
      }

      // Validar DNI/NIE
      if (!validateDNIorNIE(value)) {
        dniError.value = t('profile.dniErrorInvalid')
        return false
      }

      dniError.value = ''
      dniValidationMessage.value = t('profile.dniValidationSuccess')
      return true
    }

    const handleDniInput = (event) => {
      let value = event.target.value.toUpperCase().trim()
      
      // Eliminar espacios y caracteres no válidos
      value = value.replace(/[^0-9A-Z]/g, '')
      
      // Limpiar mensaje de validación mientras se escribe
      dniValidationMessage.value = ''

      userProfile.value.dni = value
      // No validar aquí inmediatamente, solo al enviar o al perder foco si se desea
      if (value.length === 9 || (value.length === 8 && /^[XYZ]/.test(value))) {
          validateDniInput(value)
      }
    }

    const updateProfile = async () => {
      try {
        setLoading(true)
        const parsedUser = JSON.parse(localStorage.getItem('authUser'))
        
        if (!parsedUser || !parsedUser.uid) {
          showToast(t('profile.error.noAuthenticated'))
          return
        }

        // Usar la propiedad .value de isEditing
        if (isEditing.value && !validateDni(userProfile.value.dni)) return

        // Si es un agente, validar campos específicos
        if (userProfile.value.userType === 'agent' && isEditing.value) {
          // Aquí podrías añadir validaciones específicas para agentes
          // Por ahora solo nos aseguramos de que los campos existan
          userProfile.value.agencyName = userProfile.value.agencyName || '';
          userProfile.value.professionalLicense = userProfile.value.professionalLicense || '';
          userProfile.value.managedOwners = userProfile.value.managedOwners || [];
        }

        // Prepare update data
        const updateData = {
          userType: userProfile.value.userType,
          fullName: userProfile.value.fullName,
          dni: userProfile.value.dni,
          email: userProfile.value.email,
        }

        // Add bank account for owners
        if (userProfile.value.userType === 'owner') {
          // Validar IBAN antes de guardar
          if (userProfile.value.bankNumber) {
            const ibanValidation = validateSpanishIBAN(userProfile.value.bankNumber.replace(/\s/g, ''))
            if (!ibanValidation.isValid) {
              showToast(`Error en IBAN: ${ibanValidation.error}`)
              return
            }
            updateData.bankNumber = ibanValidation.iban // Usar IBAN limpio
          } else {
            updateData.bankNumber = userProfile.value.bankNumber
          }
        }

        // Add applicant specific fields
        if (userProfile.value.userType === 'applicant') {
          updateData.applicantDescription = userProfile.value.applicantDescription || null
          updateData.monthlyIncome = userProfile.value.monthlyIncome || null
          updateData.address = userProfile.value.address || null
          updateData.withOtherPerson = userProfile.value.withOtherPerson || false
          updateData.otherPersonName = userProfile.value.otherPersonName || null
          updateData.otherPersonDni = userProfile.value.otherPersonDni || null
        }

        // Add agent specific fields
        if (userProfile.value.userType === 'agent') {
          updateData.agencyName = userProfile.value.agencyName || '';
          updateData.professionalLicense = userProfile.value.professionalLicense || '';
          updateData.managedOwners = userProfile.value.managedOwners || [];
        }

        // Update Firestore
        await updateDoc(doc(db, 'users', parsedUser.uid), updateData)
        
        // ✅ NUEVO: Actualizar Firebase Auth displayName para que se refleje en la navbar
        const auth = getAuth()
        const currentUser = auth.currentUser
        if (currentUser && userProfile.value.fullName) {
          try {
            await updateAuthProfile(currentUser, {
              displayName: userProfile.value.fullName
            })
            // Forzar recarga del usuario para disparar onAuthStateChanged en App.vue
            await currentUser.reload()
          } catch (authError) {
            console.error('Error actualizando displayName en Firebase Auth:', authError)
            // No fallamos la operación completa si solo falla la actualización de Auth
          }
        }
        
        // Update local state
        isEditing.value = false
        showToast(t('Nombre cambiado correctamente'))
      } catch (error) {
        console.error('Error actualizando perfil:', error)
        showToast(t('profile.error.update'))
      } finally {
        setLoading(false)
      }
    }

    const handleFileUpload = async (side) => {
      const fileInputId = 'dniFrontPicture'
      const file = document.getElementById(fileInputId).files[0]
      if (file) {
        try {
          const auth = getAuth()
          const user = auth.currentUser
          const dniPictureRef = storageRef(storage, `dni_pictures/${user.uid}/front`)
          await uploadBytes(dniPictureRef, file)
          const dniPictureUrl = await getDownloadURL(dniPictureRef)
          userProfile.value.dniFrontPictureUrl = dniPictureUrl
        } catch (error) {
          console.error(`Error al cargar la foto del DNI (${side}):`, error)
          toastMessage.value = t('profile.uploadError', { side, error: error.message })
        }
      }
    }

    const toggleEdit = () => {
      isEditing.value = true
    }

    const cancelEdit = () => {
      isEditing.value = false
      userProfile.value = { ...originalProfile.value }
      newPassword.value = ''
      confirmNewPassword.value = ''
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString()
    }

    const formatBankNumber = (event) => {
      const formattedValue = formatIBANInput(event.target.value)
      userProfile.value.bankNumber = formattedValue
      
      // Validar IBAN en tiempo real
      if (formattedValue.length >= 4) {
        const validation = validateSpanishIBAN(formattedValue.replace(/\s/g, ''))
        if (!validation.isValid) {
          bankNumberError.value = validation.error
        } else {
          bankNumberError.value = ''
        }
      } else {
        bankNumberError.value = ''
      }
    }

    // Watch for changes in user type
    watch(() => userProfile.value.userType, (newType) => {
      if (newType === 'applicant') {
        userProfile.value.bankNumber = null
      }
    })

    onMounted(fetchUserProfile)

    const showToast = (message) => {
      toastMessage.value = message
      setTimeout(() => {
        toastMessage.value = ''
      }, 3000)
    }

    // Funciones de validación que faltaban
    const setLoading = (value) => {
      isLoading.value = value;
    };

    const validateDni = (dni) => {
      if (!dni) return false;
      
      // Formato básico DNI (8 números + 1 letra) o NIE (X/Y/Z + 7 números + 1 letra)
      const dniRegex = /^[0-9XYZ][0-9]{7}[A-Z]$/i;
      return dniRegex.test(dni.replace(/\s/g, ''));
    };

    // Funciones para cambio de email
    const closeEmailChangeModal = () => {
      showEmailChangeModal.value = false;
      emailChangeStep.value = 'input';
      newEmail.value = '';
      emailChangeError.value = '';
      isChangingEmail.value = false;
    };

    // Función para generar token seguro
    const generateSecureToken = () => {
      const array = new Uint8Array(32)
      crypto.getRandomValues(array)
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
    };

    const requestEmailChange = async () => {
      emailChangeError.value = '';
      
      // Validaciones básicas
      if (!newEmail.value) {
        emailChangeError.value = 'El nuevo email es requerido.';
        return;
      }
      
      if (newEmail.value === userProfile.value.email) {
        emailChangeError.value = 'El nuevo email debe ser diferente al actual.';
        return;
      }
      
      try {
        isChangingEmail.value = true;
        
        // Verificar que el nuevo email no esté en uso
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', newEmail.value));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          emailChangeError.value = 'Este correo electrónico ya está en uso.';
          return;
        }
        
        // Obtener el usuario actual
        const auth = getAuth();
        const currentUser = auth.currentUser;
        
        if (!currentUser) {
          emailChangeError.value = 'Error de autenticación. Por favor, inicia sesión de nuevo.';
          return;
        }
        
        // Generar token de cambio de email
        const emailChangeToken = generateSecureToken();
        const tokenCreatedAt = new Date();
        
        // Guardar token en Firestore
        await updateDoc(doc(db, 'users', currentUser.uid), {
          pendingEmailChange: newEmail.value,
          emailChangeToken: emailChangeToken,
          emailChangeTokenCreatedAt: tokenCreatedAt
        });
        
        // Enviar email de verificación al nuevo email
        const token = await currentUser.getIdToken();
        
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/email/send-email-change-verification`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            currentEmail: userProfile.value.email,
            newEmail: newEmail.value,
            userName: userProfile.value.fullName || userProfile.value.email,
            emailChangeToken: emailChangeToken,
            userId: currentUser.uid
          })
        });

        const data = await response.json();
        
        if (data.success) {
          emailChangeStep.value = 'sent';
        } else {
          emailChangeError.value = data.message || 'Error al enviar el correo de verificación.';
        }
        
      } catch (error) {
        console.error('Error al solicitar cambio de email:', error);
        emailChangeError.value = 'Error de conexión. Por favor, intenta más tarde.';
      } finally {
        isChangingEmail.value = false;
      }
    };

    return {
      t,
      userProfile,
      newPassword,
      confirmNewPassword,
      updateProfile,
      handleFileUpload,
      isEditing,
      toggleEdit,
      cancelEdit,
      formatDate,
      toastMessage,
      showToast,
      formatBankNumber,
      dniError,
      dniValidationMessage,
      handleDniInput,
      setLoading,
      validateDni,
      isLoading,
      bankNumberError,
      // Propiedades de managedOwner
      isManagedOwner,
      managedOwnerData,
      agentData,
      isLoadingAgent,
      // Cambio de email
      showEmailChangeModal,
      emailChangeStep,
      newEmail,
      emailChangeError,
      isChangingEmail,
      closeEmailChangeModal,
      requestEmailChange
    }
  }
}
</script>

<style scoped>
/* Ensure the date picker has proper z-index and positioning for mobile */
@media (max-width: 640px) {
  .absolute.z-10 {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90% !important;
    max-width: 350px;
  }
}
</style>

